import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import ApiService from "../services/ApiService";
import "./ProfileCompletion.css";

const ProfileCompletion = ({ onComplete }) => {
  const { currentUser, updateUserProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [formData, setFormData] = useState({
    carNumber: "",
    carModel: "",
    mobileNumber: "",
    residencyId: "",
    blockId: "",
    flatId: "",
  });
  // Community data
  const [residencies, setResidencies] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [flats, setFlats] = useState([]);

  // Helper functions for loading data
  const loadResidencies = useCallback(async () => {
    try {
      const response = await ApiService.getResidencies();
      setResidencies(response.data || []);
    } catch (error) {
      console.error("Error loading residencies:", error);
      setError("Failed to load residencies");
    }
  }, []);

  const loadBlocks = useCallback(async (residencyId) => {
    try {
      const response = await ApiService.getBlocks(residencyId);
      setBlocks(response.data || []);
    } catch (error) {
      console.error("Error loading blocks:", error);
    }
  }, []);

  const loadFlats = useCallback(async (blockId) => {
    try {
      const response = await ApiService.getFlats(blockId);
      setFlats(response.data || []);
    } catch (error) {
      console.error("Error loading flats:", error);
    }
  }, []);

  const loadExistingProfile = useCallback(async () => {
    if (!currentUser) return;

    try {
      setIsLoadingExisting(true);
      const userProfile = await ApiService.getUserProfile(currentUser.uid);
      if (userProfile.success && userProfile.data) {
        const profile = userProfile.data;
        const existingData = {
          carNumber: profile.carNumber || "",
          carModel: profile.carModel || "",
          mobileNumber: profile.mobileNumber || "",
          residencyId: profile.residencyId?.toString() || "",
          blockId: profile.blockId?.toString() || "",
          flatId: profile.flatId?.toString() || "",
        };

        setFormData(existingData);

        // If residency is already selected, load blocks
        if (existingData.residencyId) {
          await loadBlocks(existingData.residencyId);

          // If block is already selected, load flats
          if (existingData.blockId) {
            await loadFlats(existingData.blockId);
          }
        }

        console.log("Loaded existing profile data:", existingData);
      }
    } catch (error) {
      console.error("Error loading existing profile:", error);
    } finally {
      setIsLoadingExisting(false);
    }
  }, [currentUser, loadBlocks, loadFlats]);

  // Load initial data and existing profile
  useEffect(() => {
    const loadData = async () => {
      await loadResidencies();
      await loadExistingProfile();
    };

    if (currentUser) {
      loadData();
    }
  }, [currentUser, loadResidencies, loadExistingProfile]);

  // Handle residency change - load blocks and reset dependent fields
  useEffect(() => {
    if (formData.residencyId && !isLoadingExisting) {
      loadBlocks(formData.residencyId);
      setFormData((prev) => ({ ...prev, blockId: "", flatId: "" }));
    }
  }, [formData.residencyId, isLoadingExisting, loadBlocks]);

  // Handle block change - load flats and reset flat field
  useEffect(() => {
    if (formData.blockId && !isLoadingExisting) {
      loadFlats(formData.blockId);
      setFormData((prev) => ({ ...prev, flatId: "" }));
    }
  }, [formData.blockId, isLoadingExisting, loadFlats]);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate all fields are filled
    if (!formData.carNumber.trim()) {
      setError("Car number is required");
      return;
    }
    if (!formData.carModel.trim()) {
      setError("Car model is required");
      return;
    }
    if (!formData.mobileNumber.trim()) {
      setError("Mobile number is required");
      return;
    }
    if (!/^[6-9]\d{9}$/.test(formData.mobileNumber.trim())) {
      setError("Please enter a valid 10-digit mobile number starting with 6-9");
      return;
    }
    if (!formData.residencyId) {
      setError("Please select a residency");
      return;
    }
    if (!formData.blockId) {
      setError("Please select a block");
      return;
    }
    if (!formData.flatId) {
      setError("Please select a flat");
      return;
    }
    try {
      setLoading(true);
      // Update user profile with all details
      const updateData = {
        carNumber: formData.carNumber.trim(),
        carModel: formData.carModel.trim(),
        mobileNumber: formData.mobileNumber.trim(),
        residencyId: parseInt(formData.residencyId),
        blockId: parseInt(formData.blockId),
        flatId: parseInt(formData.flatId),
      };

      await updateUserProfile(updateData);

      // Call onComplete to proceed to next step
      onComplete();
    } catch (error) {
      console.error("Error updating profile:", error);
      setError(error.message || "Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-completion">
      <div className="profile-completion-container">
        <div className="profile-completion-header">
          <h1>Complete Your Profile</h1>
          <p>
            Please provide your car details and community information to
            continue
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="profile-completion-form">
          {/* Car Details Section */}
          <div className="form-section">
            <h3>Car Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="carNumber">Car Number *</label>
                <input
                  type="text"
                  id="carNumber"
                  name="carNumber"
                  value={formData.carNumber}
                  onChange={handleInputChange}
                  placeholder="e.g., KA01AB1234"
                  required
                />
              </div>{" "}
              <div className="form-group">
                <label htmlFor="carModel">Car Model *</label>
                <input
                  type="text"
                  id="carModel"
                  name="carModel"
                  value={formData.carModel}
                  onChange={handleInputChange}
                  placeholder="e.g., Honda City"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="mobileNumber">Mobile Number *</label>{" "}
                <input
                  type="tel"
                  id="mobileNumber"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleInputChange}
                  placeholder="e.g., 9876543210"
                  pattern="[6-9][0-9]{9}"
                  maxLength="10"
                  required
                />
              </div>
            </div>
          </div>

          {/* Community Details Section */}
          <div className="form-section">
            <h3>Community Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="residencyId">Residency *</label>
                <select
                  id="residencyId"
                  name="residencyId"
                  value={formData.residencyId}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Residency</option>
                  {residencies.map((residency) => (
                    <option key={residency.id} value={residency.id}>
                      {residency.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="blockId">Block *</label>
                <select
                  id="blockId"
                  name="blockId"
                  value={formData.blockId}
                  onChange={handleInputChange}
                  disabled={!formData.residencyId}
                  required
                >
                  <option value="">Select Block</option>
                  {blocks.map((block) => (
                    <option key={block.id} value={block.id}>
                      {block.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="flatId">Flat *</label>
                <select
                  id="flatId"
                  name="flatId"
                  value={formData.flatId}
                  onChange={handleInputChange}
                  disabled={!formData.blockId}
                  required
                >
                  <option value="">Select Flat</option>
                  {flats.map((flat) => (
                    <option key={flat.id} value={flat.id}>
                      {flat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>{" "}
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? "Updating Profile..." : "Complete Profile"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileCompletion;
